import DataMap from 'assets/components/dataMap/DataMap';
import FilterForm from 'assets/components/filters/filterForm/FilterForm';
import ThemeFilterFormDrawer from 'assets/components/filters/filterForm/themes/Theme.FilterForm.Drawer';
import Icon from 'assets/components/icon/Icon';
import SelectInput from 'assets/components/inputs/select/SelectInput';
import Page from 'assets/components/page/Page';
import ThemePageList from 'assets/components/page/themes/Theme.Page.List';
import Pagination from 'assets/components/pagination/Pagination';
import ThemePaginationRounded from 'assets/components/pagination/themes/Theme.Pagination.Rounded';
import ThemeTableSimple from 'assets/components/table/themes/Theme.Table.Simple';
import useApi from 'assets/hooks/api/useApi';
import usePageRouter from 'assets/hooks/pageRouter/usePageRouter';
import { getLocales } from 'assets/locales/Locale';
import { useAuthStore } from 'assets/providers/authStore/Provider.AuthStore';
import ErrorPage from 'assets/templates/errorPage/ErrorPage';
import { defaultLimit, defaultOffset, HttpStatus, managementHomeRoute } from 'config/Api.Config';
import { AuthPermission } from 'config/Auth.Config';
import { get } from 'lodash';
import Application from 'models/core/application/Model.Application';
import Category from 'models/core/category/Model.Category';
import Permission from 'models/core/permission/Model.Permission';
import { isMobile } from 'react-device-detect';

enum Actions {
  EDIT = 'edit',
  TOGGLE = 'toggle',
}
export type Verify = Utils.VerifyExtends<Module.Management.Permission.Actions, Utils.ValueOf<typeof Actions>>;
export const permissionIndexRoute = managementHomeRoute + '/permission/:action?/:id?';
export default function PermissionIndex() {
  const { lang } = getLocales();
  const { hasAccess, user, permissions: currentPermissions, isDealerOrDistributor } = useAuthStore();
  const canView = hasAccess(AuthPermission.viewPermission);
  const { query, updateQuery } = usePageRouter<Module.Management.Permission.Params, Module.Management.Permission.Query>(
    { route: permissionIndexRoute }
  );
  const {
    body: filters,
    payload,
    execute: reload,
    reset,
    isExecuting,
  } = useApi({
    action: Permission.list,
    default: { limit: defaultLimit, offset: defaultOffset },
    body: { ...query.list, userId: user?.id },
    callback: (req) => updateQuery({ ...query, list: req }),
    wait: !canView || isDealerOrDistributor,
  });

  const permissionsApi = useApi({ action: Permission.list });
  const permissions = permissionsApi.payload?.data;
  const applicationsApi = useApi({ action: Application.list });
  const applications = applicationsApi.payload?.data;
  const categoryApi = useApi({ action: Category.list });
  const categories = categoryApi.payload?.data;

  if (!canView) return <ErrorPage status={HttpStatus.unauthorized} />;

  const Filters = (
    <FilterForm
      subcomponents={ThemeFilterFormDrawer}
      search={filters?.search}
      onSearch={(search) => reload((body) => ({ ...body, search, offset: 0 }))}
      onSortById={(sort) => reload((body) => ({ ...body, ...sort }))}
      filters={filters}
      onFilter={(newFilers) => reload({ ...newFilers, offset: 0 })}
      onReset={reset}
      render={({ tempFilters, setFilters }) => (
        <>
          <SelectInput
            name="applicationId"
            label={lang.applications}
            data={applications?.map((item) => ({
              id: item.id,
              title: item.name,
              icon: <Icon class="fas-cloud" />,
            }))}
            isMultiselect
            value={tempFilters?.applicationId}
            onChange={({ applicationId }) => setFilters({ ...tempFilters, applicationId })}
            icon={<Icon class="fas-cloud" />}
          />
          <SelectInput
            name="categoryId"
            label={lang.categories}
            data={categories?.map((item) => ({
              id: item.id,
              title: item.name,
              icon: <Icon class="if-sub-listing" />,
            }))}
            isMultiselect
            value={tempFilters?.categoryId}
            onChange={({ categoryId }) => setFilters({ ...tempFilters, categoryId })}
            icon={<Icon class="if-sub-listing" />}
          />
        </>
      )}
    />
  );
  return (
    <>
      <Page
        class={ThemePageList}
        isLoading={isExecuting}
        header={
          <>
            <h2 style={{ marginRight: 'auto' }} children={lang.permissions} />
            {isMobile && Filters}
          </>
        }
        subHeader={!isMobile && Filters}
        render={
          <table className={ThemeTableSimple}>
            <thead>
              <tr>
                <th>{lang.name}</th>
                <th>{lang.description}</th>
                <th>{lang.category}</th>
                <th>{lang.application}</th>
              </tr>
            </thead>
            <tbody>
              <DataMap
                data={
                  isDealerOrDistributor ? permissions?.filter((p) => get(currentPermissions, p.key)) : payload?.data
                }
                render={({ data: permission }) => (
                  <tr key={permission.id}>
                    <td>
                      <b>{permission.name}</b>
                    </td>
                    <td>{permission.description}</td>
                    <td>{permission.category?.name}</td>
                    <td>{permission.application?.name}</td>
                  </tr>
                )}
              />
            </tbody>
          </table>
        }
        footer={
          !canView ? null : (
            <Pagination
              class={ThemePaginationRounded}
              count={payload?.count}
              offset={filters?.offset}
              limit={filters?.limit}
              onLimitChange={(limit) => reload((body) => ({ ...body, limit, offset: 0 }))}
              onOffsetChange={(offset) => reload((body) => ({ ...body, offset }))}
            />
          )
        }
      />
    </>
  );
}
